import * as THREE from "three";

/**
 * The scene's "look": everything about light, air and grade in one object so the whole picture can be tuned as a
 * unit (and switched live from the debug hook for side-by-side renders). Values are the ones the HQ set is judged
 * against, not per-model tweaks — see art/SHADING.md for the material contract this sits on top of.
 */
export interface Look {
  name: string;
  /** Renderer exposure (applied inside the tone map). */
  exposure: number;
  /**
   * Tone mapping. ACES Filmic stays the default after evaluating Khronos PBR Neutral for the Phase 1 grade fix
   * (lighting review F6 proposed Neutral for hue fidelity). Worked through on the captured meadow grass (the grade and
   * tone map run on the CPU in a node script against the pixel measured in the renders): with the F1 grade fix, ACES at
   * 1.32 lands the grass at hue 92°, saturation 0.72, value 0.55; Neutral, exposed to the same mid-grey (≈1.6), lands
   * it at hue 87°, saturation 0.85, value 0.53. Neutral keeps hue a little better, but it keeps mid-tone SATURATION
   * too, and over-saturated grass is half of what F1 is fixing; ACES's filmic shoulder desaturates it toward the
   * Kingshot target (0.47). ACES also does not push greens toward cyan in this rig (a pure green goes 110° → 100°,
   * toward yellow, not cyan). So: ACES, with "neutral" kept selectable per look for the LUT work in Phase 2 — a LUT
   * authored from the references can take over the colour, and then Neutral's clean hue base is the better input.
   */
  toneMapping?: "aces" | "neutral";
  /** Key light. Elevation in degrees; azimuth is relative to the camera (see SceneRig.placeSun). */
  sun: { color: number; intensity: number; elevation: number };
  /** A cool fill from the opposite side so shadowed faces keep their form. */
  fill: { color: number; intensity: number };
  hemi: { sky: number; ground: number; intensity: number };
  /** Image-based light for the PBR metals and glossy paint. */
  envIntensity: number;
  envSky: number; envHorizon: number; envGround: number; envSun: number;
  /** The sky DOME's zenith colour (it used to reuse envSky; they are separate so the IBL can be de-blued alone). */
  skyTop?: number;
  /** Sky clear colour and the distance fog band (in map radii past the look point) + colour. */
  sky: number; fog: number; fogNear: number; fogFar: number;
  /** Ground macro-variation: how far tiles drift toward the warm / cool tints by world-space noise. */
  groundVariation: number; groundWarm: number; groundCool: number;
  normalScale: number;
  /** Post: bloom, screen-space AO, grade. */
  /**
   * `threshold` is on the scene-referred buffer (before exposure); `knee` is the soft ramp above it (default 0.01, the
   * stock hard edge). Lit diffuse tops out around 1.0 under the painterly sun, so a threshold of 1.1 leaves bloom to
   * what is actually emissive: the ×3 glow slots, lava, the HDR VFX, the chest beam.
   */
  bloom: { strength: number; radius: number; threshold: number; knee?: number };
  ao: { enabled: boolean; radius: number; intensity: number };
  grade: { contrast: number; saturation: number; vignette: number; shadows: number; highlights: number; lift: number };
}

export const LOOKS: Record<string, Look> = {
  /** What shipped up to 2026-09-09: flat white key, no IBL, no grade. */
  flat: {
    name: "flat",
    exposure: 1.05,
    sun: { color: 0xfff1dc, intensity: 1.6, elevation: 52 },
    fill: { color: 0x9fb8ff, intensity: 0 },
    hemi: { sky: 0xcfe3ff, ground: 0x6b5a8a, intensity: 0.55 },
    envIntensity: 0, envSky: 0xbcd4ff, envHorizon: 0xf2e6d0, envGround: 0x5a4a3a, envSun: 0xfff0d0,
    sky: 0xb9c8e6, fog: 0xb9c8e6, fogNear: 0.9, fogFar: 2.6,
    groundVariation: 0, groundWarm: 0x9fb04a, groundCool: 0x3f7a4a, normalScale: 0.7,
    bloom: { strength: 0.55, radius: 0.4, threshold: 0.86 },
    ao: { enabled: false, radius: 0.5, intensity: 1 },
    grade: { contrast: 1, saturation: 1, vignette: 0, shadows: 0xffffff, highlights: 0xffffff, lift: 0 },
  },
  /** A — warm afternoon, painterly: golden key, blue-violet shadows, rich grass, soft mist. */
  painterly: {
    name: "painterly",
    exposure: 1.32,
    sun: { color: 0xffd9a8, intensity: 2.8, elevation: 50 },
    fill: { color: 0x8fb4ff, intensity: 0.7 },
    // Lighting review F1: the hemisphere sky and the IBL sky were two of the three blue sources stacking on the grass
    // (the third was the grade). hemi 0xa9c6ff → 0xc9d8ee, envSky 0x5f8fd8 → 0x86a6d8; the dome keeps its blue (skyTop).
    hemi: { sky: 0xc9d8ee, ground: 0x7a5a40, intensity: 0.95 },
    envIntensity: 0.75, envSky: 0x86a6d8, envHorizon: 0xffe3c0, envGround: 0x6a5238, envSun: 0xfff2d8, skyTop: 0x5f8fd8,
    sky: 0xa9c2de, fog: 0xa8bcd8, fogNear: 0.8, fogFar: 2.6,
    groundVariation: 0.16, groundWarm: 0xb8b84a, groundCool: 0x2f7a52, normalScale: 1.1,
    bloom: { strength: 0.5, radius: 0.5, threshold: 1.1, knee: 0.15 },
    ao: { enabled: true, radius: 0.9, intensity: 1.4 },
    // saturation 1.15 → 1.0 and shadows 0xc4ccff → 0xdde2ff (F1); the split-tone weight is now display-referred (postfx.ts)
    grade: { contrast: 1.08, saturation: 1.0, vignette: 0.22, shadows: 0xdde2ff, highlights: 0xfff0d0, lift: 0.015 },
  },
  /** B — golden hour, dramatic: low orange sun, long shadows, deep teal shadows, heavy mist, strong vignette. */
  goldenHour: {
    name: "goldenHour",
    exposure: 1.0,
    sun: { color: 0xffb86a, intensity: 3.2, elevation: 34 },
    fill: { color: 0x6f9cff, intensity: 0.7 },
    hemi: { sky: 0x8fb0ff, ground: 0x6b4a2a, intensity: 0.6 },
    envIntensity: 0.9, envSky: 0x4f78c8, envHorizon: 0xffc890, envGround: 0x5a4028, envSun: 0xffd6a0, skyTop: 0x4f78c8,
    sky: 0xe8c9b0, fog: 0xe4c9b8, fogNear: 0.6, fogFar: 2.0,
    groundVariation: 0.32, groundWarm: 0xc8a840, groundCool: 0x2e6a4a, normalScale: 1.2,
    bloom: { strength: 0.65, radius: 0.55, threshold: 1.05, knee: 0.15 },
    ao: { enabled: true, radius: 1.0, intensity: 1.6 },
    // same over-saturation as painterly had (1.12 on top of ACES); the teal shadows are this preset's character and now
    // only reach real shadows (display-referred weight)
    grade: { contrast: 1.2, saturation: 1.0, vignette: 0.38, shadows: 0x9fb4e0, highlights: 0xffe6b8, lift: 0.0 },
  },
  /** C — overcast highland: cool soft key, high-contrast grade, strong AO, mist in every hollow. */
  highland: {
    name: "highland",
    exposure: 1.15,
    sun: { color: 0xfff4e6, intensity: 2.0, elevation: 58 },
    fill: { color: 0xa8c4ff, intensity: 0.8 },
    hemi: { sky: 0xc4d6f4, ground: 0x5a5a4a, intensity: 0.9 },
    envIntensity: 0.65, envSky: 0x7f9cc8, envHorizon: 0xe8ecf0, envGround: 0x4a4a3a, envSun: 0xffffff, skyTop: 0x7f9cc8,
    sky: 0xdde5ee, fog: 0xd6dfea, fogNear: 0.55, fogFar: 1.9,
    groundVariation: 0.24, groundWarm: 0xa0a850, groundCool: 0x2b6a58, normalScale: 1.15,
    bloom: { strength: 0.4, radius: 0.5, threshold: 1.1, knee: 0.15 },
    ao: { enabled: true, radius: 1.1, intensity: 1.9 },
    grade: { contrast: 1.22, saturation: 1.0, vignette: 0.32, shadows: 0xaebad8, highlights: 0xfff6ea, lift: 0.0 },
  },
};

export const DEFAULT_LOOK = "painterly";

/**
 * Hero-moment target for Phase 2's "boss dusk" (lighting review §4.1): not a look to switch to with `setLook` (that
 * rebuilds the PMREM and hitches), but a target for `SceneRig.blendLook(BOSS_DUSK, t)`, which lerps only the lights,
 * exposure, fog and grade. Numbers from the review: sun 2.8 → 1.3 toward 0xff9a5a, exposure 1.32 → 1.05.
 */
export const BOSS_DUSK: Look = {
  ...LOOKS.painterly,
  name: "bossDusk",
  exposure: 1.05,
  sun: { color: 0xff9a5a, intensity: 1.3, elevation: 50 },
  fill: { color: 0x7f9cff, intensity: 0.55 },
  hemi: { sky: 0x9fa8d8, ground: 0x5a4030, intensity: 0.75 },
  // the horizon warms and dims (sky dome horizon, fog, map-edge haze and the backstop all follow `sky`), the zenith deepens
  sky: 0xc4a08e, fog: 0xb89c9c, skyTop: 0x46558f, envSun: 0xffb070,
  bloom: { strength: 0.6, radius: 0.55, threshold: 1.0, knee: 0.15 },
  grade: { contrast: 1.12, saturation: 0.95, vignette: 0.34, shadows: 0xc8ccf0, highlights: 0xffd8b0, lift: 0.0 },
};

/**
 * "The sun breaks through" (lighting review §4.1, the reverse beat on the boss kill): the overshoot target the dusk lifts
 * THROUGH — a brighter, warmer sun and exposure than the afternoon, a gently warm grade — for about three seconds
 * (BossDusk), then back to painterly. Same rule as BOSS_DUSK: a blend target, never `setLook`.
 */
export const SUN_BREAK: Look = {
  ...LOOKS.painterly,
  name: "sunBreak",
  exposure: 1.5,
  sun: { color: 0xffd08a, intensity: 3.6, elevation: 50 },
  fill: { color: 0x9fb8ff, intensity: 0.75 },
  hemi: { sky: 0xe2dccb, ground: 0x7a5a40, intensity: 1.0 },
  sky: 0xc4c6d4, fog: 0xc0c0cc, skyTop: 0x6a98dc, envSun: 0xfff0c8,
  bloom: { strength: 0.62, radius: 0.5, threshold: 1.05, knee: 0.15 },
  grade: { contrast: 1.1, saturation: 1.06, vignette: 0.16, shadows: 0xe6e4ff, highlights: 0xffe4b8, lift: 0.02 },
};

/**
 * The reward chest's own renderer (chestStage.ts, a separate transparent canvas over the parchment) — tone and bloom
 * only; the stage itself is the ui work's. Its burst read as a white blow-out: the ×3 HDR glow slots, the rays' core
 * (up to ~3.5 linear) and the additive burst VFX all sat far above a 0.86 hard-knee bloom threshold at strength 0.75,
 * then ACES at 1.05 clipped the lot. These numbers keep the core bright but coloured: exposure 0.85, bloom only on
 * what is really hot (threshold 1.4 with a soft 0.5 knee) at a lower strength and tighter radius.
 */
export const CHEST_TONE = { exposure: 0.85, bloom: { strength: 0.45, radius: 0.3, threshold: 1.4, knee: 0.5 } } as const;

/** A small gradient-sky scene rendered to a PMREM environment: sky above, warm horizon, dark ground, a sun blob. */
export function buildEnvironment(renderer: THREE.WebGLRenderer, look: Look): THREE.Texture {
  const scene = new THREE.Scene();
  const geo = new THREE.SphereGeometry(50, 32, 16);
  const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    uniforms: { sky: { value: new THREE.Color(look.envSky) }, horizon: { value: new THREE.Color(look.envHorizon) }, ground: { value: new THREE.Color(look.envGround) }, sun: { value: new THREE.Color(look.envSun) }, sunDir: { value: new THREE.Vector3(-0.5, 0.6, 0.6).normalize() } },
    vertexShader: `varying vec3 vDir; void main() { vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `varying vec3 vDir; uniform vec3 sky, horizon, ground, sun, sunDir;
      void main() {
        float h = vDir.y;
        vec3 c = h > 0.0 ? mix(horizon, sky, pow(h, 0.55)) : mix(horizon, ground, pow(-h, 0.7));
        float s = max(0.0, dot(vDir, sunDir));
        c += sun * (pow(s, 240.0) * 6.0 + pow(s, 8.0) * 0.35);
        gl_FragColor = vec4(c, 1.0);
      }`,
  });
  scene.add(new THREE.Mesh(geo, mat));
  const pmrem = new THREE.PMREMGenerator(renderer);
  const tex = pmrem.fromScene(scene, 0.02).texture;
  pmrem.dispose(); geo.dispose(); mat.dispose();
  return tex;
}
